import type { IncomingPairRequest } from "@/src/infrastructure/Services/WebRTCService";
import type { NotificationAction } from "./studioNotifications";

export type GuestStatus =
  "pending" | "connecting" | "connected" | "rejected" | "expired" | "failed";
export interface GuestNotice {
  id: string;
  category: "guests";
  name: string;
  createdAt: string;
  expiresAt: string;
  status: GuestStatus;
  readAt: string | null;
  archivedAt: string | null;
}
type Respond = (request: IncomingPairRequest, accept: boolean) => Promise<void>;

// Session-only history: never enters a workspace snapshot or uploads guest data.
export class GuestNotificationFeed {
  private items: GuestNotice[] = [];
  private listeners = new Set<() => void>();
  private pending = new Map<
    string,
    {
      request: IncomingPairRequest;
      respond: Respond;
      timer: ReturnType<typeof setTimeout>;
    }
  >();
  private seen = new Set<string>();
  private generation = 0;

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };
  snapshot = () => this.items;
  private publish(items: GuestNotice[]) {
    this.items = items;
    this.listeners.forEach((listener) => listener());
  }
  private status(id: string, status: GuestStatus) {
    this.publish(
      this.items.map((item) => (item.id === id ? { ...item, status } : item)),
    );
  }
  private expire(id: string) {
    const pending = this.pending.get(id);
    if (!pending) return;
    clearTimeout(pending.timer);
    this.pending.delete(id);
    this.status(id, "expired");
  }

  sync(requests: IncomingPairRequest[], respond: Respond) {
    const current = new Set(requests.map((request) => request.id));
    for (const id of this.pending.keys()) if (!current.has(id)) this.expire(id);
    for (const request of requests) {
      if (this.seen.has(request.id)) continue;
      this.seen.add(request.id);
      // Bound de-duplication memory as well as the displayed session history.
      if (this.seen.size > 2000)
        this.seen.delete(this.seen.values().next().value!);
      const timer = setTimeout(() => {
        if (!this.pending.has(request.id)) return;
        this.expire(request.id);
        void respond(request, false).catch(() => {});
      }, 30_000);
      this.pending.set(request.id, { request, respond, timer });
      const history = this.items
        .filter(
          (item) => item.status !== "pending" && item.status !== "connecting",
        )
        .slice(0, 99);
      const active = this.items.filter(
        (item) => item.status === "pending" || item.status === "connecting",
      );
      this.publish([
        {
          id: request.id,
          category: "guests",
          name: request.guestName,
          createdAt: new Date().toISOString(),
          expiresAt: new Date(Date.now() + 30_000).toISOString(),
          status: "pending",
          readAt: null,
          archivedAt: null,
        },
        ...active,
        ...history,
      ]);
    }
  }

  async respond(id: string, accept: boolean) {
    const pending = this.pending.get(id);
    if (!pending) return;
    const generation = this.generation;
    clearTimeout(pending.timer);
    this.pending.delete(id);
    this.update([id], "read");
    this.status(id, "connecting");
    try {
      await pending.respond(pending.request, accept);
      if (generation === this.generation)
        this.status(id, accept ? "connected" : "rejected");
    } catch {
      if (generation === this.generation) this.status(id, "failed");
    }
  }

  update(ids: string[], action: NotificationAction) {
    const field =
      action === "archive" || action === "restore" ? "archivedAt" : "readAt";
    const value =
      action === "read" || action === "archive"
        ? new Date().toISOString()
        : null;
    this.publish(
      this.items.map((item) =>
        ids.includes(item.id) ? { ...item, [field]: value } : item,
      ),
    );
  }

  detach() {
    this.generation++;
    for (const entry of this.pending.values()) clearTimeout(entry.timer);
    this.pending.clear();
    this.publish(
      this.items.map((item) =>
        item.status === "pending" || item.status === "connecting"
          ? { ...item, status: "expired" }
          : item,
      ),
    );
  }
}
